import { useMemo, useState } from "react";
import {
  buddhistYear,
  displayDate,
  typeLabel,
} from "../lib/legalTaxonomy";
import { useLocale } from "../lib/LocaleProvider";

export default function ResultList({ results, selectedId, onSelect, query, error, year }) {
  const { locale, text, count } = useLocale();
  const [groupBy, setGroupBy] = useState("type");
  const [sortBy, setSortBy] = useState("relevance");
  const groups = useMemo(() => {
    const filtered = year
      ? results.filter((item) => buddhistYear(item.issued_date, item.year_be) === String(year))
      : results;
    const ordered = [...filtered];
    if (sortBy !== "relevance") {
      ordered.sort((a, b) => {
        const left = a.issued_date || "";
        const right = b.issued_date || "";
        return sortBy === "newest" ? right.localeCompare(left) : left.localeCompare(right);
      });
    }
    const grouped = new Map();
    ordered.forEach((item) => {
      const key = groupBy === "type"
        ? typeLabel(item.document_type, locale)
        : displayDate(item.issued_date, item.year_be, locale) || text("results.undated");
      if (!grouped.has(key)) grouped.set(key, []);
      grouped.get(key).push(item);
    });
    if (groupBy === "year") {
      return [...grouped.entries()].sort((a, b) =>
        sortBy === "oldest"
          ? a[0].localeCompare(b[0], locale, { numeric: true })
          : b[0].localeCompare(a[0], locale, { numeric: true }),
      );
    }
    return [...grouped.entries()];
  }, [groupBy, locale, results, sortBy, text, year]);

  const visibleCount = groups.reduce((sum, [, items]) => sum + items.length, 0);
  return (
    <aside className="results-panel" id="results" aria-label={text("results.aria")}>
      <div className="results-controls">
        <div className="panel-title">
          <div>
            <span className="eyebrow">{text("results.eyebrow")}</span>
            <h2>{text("results.count", { count: count(visibleCount) })}</h2>
          </div>
          <select
            aria-label={text("results.sort")}
            value={sortBy}
            onChange={(event) => setSortBy(event.target.value)}
          >
            <option value="relevance">{text("results.relevance")}</option>
            <option value="newest">{text("results.newest")}</option>
            <option value="oldest">{text("results.oldest")}</option>
          </select>
        </div>
        <div className="group-tabs">
          <button type="button" className={groupBy === "type" ? "active" : ""} onClick={() => setGroupBy("type")}>
            {text("results.byType")}
          </button>
          <button type="button" className={groupBy === "year" ? "active" : ""} onClick={() => setGroupBy("year")}>
            {text("results.byYear")}
          </button>
        </div>
      </div>
      {error && <p className="error-message">{error}</p>}
      {!error && visibleCount === 0 && (
        <div className="empty-state">
          <p>{query ? text("results.empty") : text("results.start")}</p>
          <span>{text("results.description")}</span>
        </div>
      )}
      {groups.map(([group, items]) => (
        <section className="result-group" key={group}>
          <header><h3>{group}</h3><span>{count(items.length)}</span></header>
          <ol className="result-list">
            {items.map((result) => (
              <li key={result.expression_id}>
                <button
                  type="button"
                  className={selectedId === result.expression_id ? "selected" : ""}
                  onClick={() => onSelect(result)}
                  aria-current={selectedId === result.expression_id ? "true" : undefined}
                >
                  <span className="result-badges">
                    <span className="type-badge">{typeLabel(result.document_type, locale)}</span>
                    {displayDate(result.issued_date, result.year_be, locale) && (
                      <span className="year-badge">{displayDate(result.issued_date, result.year_be, locale)}</span>
                    )}
                  </span>
                  <strong title={result.exact_title}>{result.title}</strong>
                  <span className="result-meta">
                    {[result.official_number, result.language?.toUpperCase()].filter(Boolean).join(" · ")}
                  </span>
                  <span className="result-snippet" dangerouslySetInnerHTML={{ __html: result.snippet || "" }} />
                  {result.review_status && (
                    <span className="review-state">
                      {result.review_status === "approved" ? text("results.approved") : text("results.underReview")}
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ol>
        </section>
      ))}
    </aside>
  );
}
